import React, { useState } from "react";

import type { ZohoStepDTO } from "../data/zohoConnectApi";
import { groupGoLiveSteps, groupStatus, type GoLiveGroup } from "../domain/goLiveGroups";
import { SequenceStepRow, Spinner } from "./ConnectionSequence";
import { CheckIcon, ChevronDownIcon, ChevronRightIcon, WarningIcon } from "./icons";
import { cn } from "./cn";

/**
 * The go-live sequence as four stages instead of twenty rows.
 *
 * Each stage is one line: an icon, a name, and how far along it is. The active
 * stage carries a second, quieter line — the step running inside it right now —
 * because that line changing every few seconds is what tells a CA the screen
 * is alive. Four silent spinners is where people start refreshing.
 *
 * Finished stages fold shut but stay clickable; the detail (what was found,
 * what was written) is one click away, never gone. A FAILED stage is forced
 * open — its summary line cannot say more than "failed", and the row inside
 * carries the actual reason and the Try again button. Collapsing a failure
 * would be hiding it.
 *
 * PAUSED is a real state here: each step runs on a request from this page, so
 * a closed tab stops the run where it stands. A stage that is part-done while
 * nothing is in flight shows a Resume button rather than a spinner that lies.
 */
export function GroupedSequence({
  title,
  steps,
  onRetry,
  onResume,
}: {
  title: string;
  steps: ZohoStepDTO[];
  /** Re-runs the sequence from the failed step. Shown inside the failed row. */
  onRetry?: () => void;
  /** Picks the run back up when it is part-done with nothing in flight. */
  onResume?: () => void;
}) {
  const [opened, setOpened] = useState<Record<string, boolean>>({});
  const groups = groupGoLiveSteps(steps);

  return (
    <div className="flex flex-col gap-4 rounded-lg border p-6">
      <h3 className="text-[17px] font-medium">{title}</h3>

      <ul className="flex flex-col">
        {groups.map((group) => (
          <GroupRow
            key={group.id}
            group={group}
            open={opened[group.id] === true}
            onToggle={() => setOpened((current) => ({ ...current, [group.id]: !(current[group.id] === true) }))}
            onRetry={onRetry}
            onResume={onResume}
          />
        ))}
      </ul>
    </div>
  );
}

function GroupRow({
  group,
  open,
  onToggle,
  onRetry,
  onResume,
}: {
  group: GoLiveGroup;
  open: boolean;
  onToggle: () => void;
  onRetry?: () => void;
  onResume?: () => void;
}) {
  const status = groupStatus(group);
  const done = group.steps.filter((step) => step.status === "done").length;
  const runningStep = group.steps.find((step) => step.status === "running") ?? null;
  // Part-done with nothing in flight: the run was interrupted, not finished.
  const paused = status === "running" && runningStep === null;
  // A failure's reason and Try again live on the step row — so the rows must show.
  const expanded = status === "failed" || open;
  const Chevron = expanded ? ChevronDownIcon : ChevronRightIcon;

  return (
    <li className="flex flex-col gap-2 border-t py-3 first:border-t-0 first:pt-0 last:pb-0">
      <button
        type="button"
        onClick={onToggle}
        aria-expanded={expanded}
        className="flex w-full items-center gap-2 text-left"
      >
        {status === "done" ? (
          <CheckIcon className="size-4 shrink-0 text-emerald-600" />
        ) : status === "failed" ? (
          <WarningIcon className="size-4 shrink-0 text-amber-600" />
        ) : status === "running" && !paused ? (
          <Spinner />
        ) : (
          <span className="size-4 shrink-0 rounded-full border-2 border-muted-foreground/20" />
        )}
        <span className={cn("text-sm font-medium", status === "pending" && "text-muted-foreground")}>
          {group.title}
        </span>
        <span className="ml-auto shrink-0 text-xs text-muted-foreground">
          {status === "done"
            ? "Done"
            : status === "failed"
              ? "Needs attention"
              : status === "running"
                ? paused
                  ? "Paused"
                  : `${done} of ${group.steps.length}`
                : ""}
        </span>
        <Chevron className="size-4 shrink-0 text-muted-foreground" />
      </button>

      {/* The live line: what is happening inside the active stage right now. */}
      {!expanded && runningStep !== null && (
        <div className="flex flex-col gap-0.5 pl-6">
          <p className="text-[13px] text-muted-foreground">{runningStep.label}…</p>
          {runningStep.detail && <p className="text-xs text-muted-foreground/70">{runningStep.detail}</p>}
        </div>
      )}

      {paused && onResume && (
        <div className="flex items-center gap-3 pl-6">
          <p className="text-[13px] text-muted-foreground">Stopped part-way. Nothing done so far is lost.</p>
          <button
            type="button"
            onClick={onResume}
            className="rounded-md border bg-background px-3 py-[5px] text-[13px] font-medium transition-colors hover:bg-muted/40"
          >
            Resume
          </button>
        </div>
      )}

      {expanded && (
        <ul className="flex flex-col gap-2 pl-6">
          {group.steps.map((step) => (
            <li key={step.key}>
              <SequenceStepRow step={step} onRetry={onRetry} showDoneDetail />
            </li>
          ))}
        </ul>
      )}
    </li>
  );
}
